import { useUser } from "@clerk/clerk-expo";
import CreditCardForm from "components/cart/CreditCardForm";
import { Tab, TabContainer } from "components/general/Tabs";
import TitledContainer from "components/general/TitledContainer";
import PageLoader from "components/general/PageLoader";
import { Box, Button, HStack, Icon, ParagraphSmall, ScrollView, Typography, View, VStack } from "components/ui";
import { useLocalSearchParams } from "expo-router";
import { MapPin } from "lucide-react-native";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Platform } from "react-native";
import { CartCheckoutLayout, canBeFulfilled } from "screens/CartCheckoutLayout";
import { useGetCartByIdQuery, useGetCartCurrentQuery, useGetCompanyQuery, usePutCartByIdMutation } from "store/abcoApi";
import { useDataLayer } from "utils/hooks/useDataLayer";
import { useIsMobile } from "utils/hooks/useIsMobile";

export default function Page() {
	const { id } = useLocalSearchParams<{ id: string }>();
	const { data: cart, isSuccess } = id ? useGetCartByIdQuery({ id: parseInt(id) }) : useGetCartCurrentQuery();
	const [activeTab, setActiveTab] = useState<string>("");
	const [updateCart] = usePutCartByIdMutation();
	const [selectedCard, setSelectedCard] = useState<any>(null);
	const { data: company, isLoading: isCompanyLoading } = useGetCompanyQuery();
	const { user } = useUser();

	const [noReplaceItems, setNoReplaceItems] = useState<Set<string>>(new Set());

	useEffect(() => {
		if (cart && isSuccess) {
			const cartLines = cart.lines || [];
			const status40Items = cartLines.filter((item: any) => canBeFulfilled(item, cart) === 40);

			if (status40Items.length > 0) {
				const status40Stockcodes = new Set<string>(status40Items.map((item: any) => item.item.stockcode));
				setNoReplaceItems(status40Stockcodes);
			}
		}
	}, [cart, isSuccess]);

	const showCreditCardForm = useMemo(() => {
		if (isCompanyLoading || !company) {
			return false;
		}
		if (company && company.disableCreditCardPayments) {
			return false;
		}
		return true;
	}, [company, isCompanyLoading]);

	const currentCompany = useMemo(() => {
		if (!user || !user.publicMetadata || !user.publicMetadata.companyData) {
			return null;
		}

		const companies = user.publicMetadata.companyData as any;
		const companyId = user.unsafeMetadata.companyId as number;

		if (companyId && companies[companyId]) {
			return companies[companyId];
		}

		return Object.values(companies)[0] || null;
	}, [user]);

	// Memoize cart ID to prevent unnecessary re-renders
	const cartId = useMemo(() => cart?.id, [cart?.id]);

	useEffect(() => {
		if (!isSuccess || !cart) {
			return;
		}

		const paymentOption = cart?.paymentOption;

		if (paymentOption) {
			setActiveTab(paymentOption.toLowerCase());
		} else if (cart?.hasAccountTerms) {
			setActiveTab("account");
		} else {
			setActiveTab("creditcard");
		}
	}, [cart?.paymentOption, cart?.hasAccountTerms, isSuccess]);

	const handlePymentTabChange = useCallback(
		(tab: string) => {
			setActiveTab(tab);
			if (cartId) {
				updateCart({
					cart: {
						paymentOption: tab,
					},
					id: cartId,
				});
			}
		},
		[cartId, updateCart],
	);

	const handleSelectedCard = useCallback(
		(card: any) => {
			// Prevent unnecessary state updates if same card
			if (selectedCard?.value === card?.value) {
				return;
			}

			setSelectedCard(card);

			if (card && cartId) {
				updateCart({
					cart: {
						paymentTokenId: card.value,
					},
					id: cartId,
				});
			}
		},
		[cartId, updateCart, selectedCard?.value],
	);

	const { addPaymentInfo } = useDataLayer();
	const [hasPushedViewCart, setHasPushedViewCart] = useState(false);

	useEffect(() => {
		if (!cart || !isSuccess || hasPushedViewCart) return;

		addPaymentInfo();

		setHasPushedViewCart(true);
	}, [cart?.id, isSuccess, hasPushedViewCart, addPaymentInfo]);

	const isMobile = useIsMobile();

	const PageContent = useCallback(() => {
		return (
			<Box paddingTop={isMobile ? 24 : 0} paddingHorizontal={isMobile ? 12 : 0} paddingBottom={isMobile ? 24 : 0}>
				<TitledContainer title="Select payment method">
					<Box position="relative" zIndex={10} paddingHorizontal={isMobile ? 0 : 16}>
						<Box paddingVertical={16}>
							<TabContainer onChange={handlePymentTabChange} selectedTab={activeTab}>
								{cart?.hasAccountTerms === true && (
									<Tab title="Terms" id="account">
										<Box padding={16} bg="$neutral25">
											<Box gap={12}>
												<Typography weight="bold">Your order will be charged to your account.</Typography>
												<Box
													// @ts-ignore
													width={"fit-content"}
													bg="$neutral50"
													borderWidth={1}
													padding={16}
													borderRadius={12}
												>
													<Button paddingLeft={isMobile ? 6 : 0} gap={8}>
														<VStack>
															<HStack alignItems="center" gap={6}>
																<Icon as={MapPin} color="$basicblack" />
																<Box flex={1}>
																	<Typography weight="bold">{currentCompany?.name || ""}</Typography>
																	<Typography>{currentCompany?.address || ""}</Typography>
																</Box>
															</HStack>
														</VStack>
													</Button>
												</Box>
											</Box>
										</Box>
									</Tab>
								)}
								{showCreditCardForm && (
									<Tab title="Credit card" id="creditcard">
										<CreditCardForm selectedCard={selectedCard} onSelectCard={handleSelectedCard} />
									</Tab>
								)}
							</TabContainer>
						</Box>
					</Box>
				</TitledContainer>
			</Box>
		);
	}, [isMobile, handlePymentTabChange, activeTab, cart?.hasAccountTerms, currentCompany?.name, currentCompany?.address, showCreditCardForm, isCompanyLoading, selectedCard, handleSelectedCard]);

	if (isMobile) {
		return (
			<CartCheckoutLayout canEdit={true} noReplaceItems={noReplaceItems}>
				<ScrollView style={{ flex: 1 }} contentContainerStyle={{ flexGrow: 1 }}>
					<PageContent />
				</ScrollView>
			</CartCheckoutLayout>
		);
	}
	return (
		<CartCheckoutLayout canEdit={true} noReplaceItems={noReplaceItems}>
			<PageContent />
		</CartCheckoutLayout>
	);
}
